import guid from 'guid';
import _ from 'lodash';
import Immutable from 'immutable';

import SCENE_TYPE from '../configs/sceneTypes';
import SCENE_CONTAINER from '../configs/sceneContainer';
import SceneFactory from '../models/SceneFactory';
import RecordFactory from '../models/RecordFactory';
import recordActions from '../actions/recordActions';
import apiActions from '../actions/apiActions';
import userSettingsActions from '../actions/userSettingsActions';
import UserSettingsStore from './UserSettingsStore';
import makeApiRequest from '../utils/makeApiRequest';
import filtersUtils from '../utils/filters';
import getFilterValues from '../utils/getFilterValues';
import {
  getRecordsKey,
  getRecordsCountKey,
  getRecordsCountNewMessagesKey,
  getAllRecordsLoadedKey,
  getLoadingKey,
  getLoadingErrorKey,
  getQueryKey,
} from '../utils/getRecordsKey';
import viewSettingsActions from '../actions/viewSettingsActions';
import { BATCH_RECORDS_STATUS } from '../configs/recordsBatchStatus';

const sceneMixin = {
  callbacks: {},
  /*
  sceneId
  type - to identity viewer (record or catalog) from this.SCENE_TYPE,
  container - popup, windowm ...
  params = {
    recordId - to identity initial record,
    catalogId - to identity initial catalog,
  },
  data = { filters, sorting, records: [{…}], visible: true, etc... },
  parentSceneId - to identity parent item (prelast child in array),
  callbacks={
    onCreate,
    onClose,
    onCreateRecord, (?)
    ...callbacks
  }
  */

  /*
    Базовые взаимодействия со сценой
  */

  /* on open records, catalogs, multimodals */
  createScene(scene, callbacks) {
    scene.sceneId = scene.sceneId || guid.raw();

    this.setIn(['scenes', scene.sceneId], SceneFactory.create(scene));

    // onCreate - дать такое название колбеку, где создается сцена
    this.setCallbacks(scene.sceneId, callbacks);

    // возвращает id сцены
    const callback = this.getCallback(scene.sceneId, 'onCreate');
    callback && callback(scene);

    this.changed();
  },

  /* on changing filters, sorting, records, etc... */
  // eslint-disable-next-line default-param-last
  updateSceneData(sceneId, data = {}, callbacks) {
    this.mergeIn(['scenes', sceneId, 'data'], data);
    callbacks && this.setCallbacks(sceneId, callbacks);
    this.changed();
  },

  updateSceneParams(sceneId, params = {}) {
    // запоминаем прошлый параметры
    const scene = this.getIn(['scenes', sceneId]);
    const catalogId = scene.getIn(['params', 'catalogId']);
    const recordId = scene.getIn(['params', 'recordId']);

    // замена параметров
    this.mergeIn(['scenes', sceneId, 'params'], params);

    /* для очистки аппстейта от закрытых записей */
    const sceneType = scene && scene.get('type');
    if (
      sceneType === SCENE_TYPE.RECORD ||
      sceneType === SCENE_TYPE.RECORDS_BATCH_UPDATE ||
      sceneType === SCENE_TYPE.RECORDS_BATCH_DELETE
    ) {
      this.clearRecord(catalogId, recordId);
    }

    this.changed();
  },

  updateSceneRecord(sceneId, catalogId, recordId, data) {
    const scene = this.getIn(['scenes', sceneId]);
    const recordKey = scene.get('records').findKey((i) => i.get('id') === recordId && i.get('catalogId') === catalogId);

    this.mergeIn(['scenes', sceneId, 'records', recordKey], data);

    this.changed();
  },

  formationFilters(viewId, scene) {
    let filters = scene.getIn(['data', 'filters']) || Immutable.Map({});
    const viewsFilters = scene.getIn(['views', viewId, 'filters']);
    filters = filtersUtils.mergeFilters(filters, viewsFilters);
    const searchText = scene.get('searchText');
    return {
      filters,
      searchText,
    };
  },

  /* удаление сцены с изменением видимости */
  deleteScene(sceneId) {
    const parentSceneId = this.getIn(['scenes', sceneId, 'parentSceneId']);
    const isActive = this.getIn(['modal', 'activeScene']) === sceneId;
    const scene = this.getIn(['scenes', sceneId]);
    const sceneContainer = scene && scene.get('container');

    if (!scene) {
      return;
    }

    const childScene = this.get('scenes').find((scene) => scene.get('parentSceneId') === sceneId);

    /* удаление из списка сцен */
    this.deleteIn(['scenes', sceneId]);

    const lastChildId = this._findSceneToDelete();

    /* могут быть кейсы удаления неактивных сцен */
    if (isActive) {
      /* в случае удаления последнего модального окна из очереди на удаление, происходит смена видимости модальног окна */
      if (lastChildId) {
        lastChildId && this.switchModal(lastChildId);
      } else {
        /* если не было найдено lastChildId, происходит удаление записи или каталога, поэтому могут быть проблемы в виде смены видимости модальных окон */
        if (sceneContainer === SCENE_CONTAINER.POPUP && childScene) {
          this.switchModal(childScene.get('sceneId'));
        } else {
          // если еще есть модальные окна, переключай на любое из них
          // this.switchFirstModal();
          const parentScene = this.getIn(['scenes', parentSceneId]);
          parentScene ? this.switchModal(parentSceneId) : this.switchFirstModal();
        }
      }
    }

    // при удалении сцены, вызывается функция подписки
    const onClose = this.getCallback(sceneId, 'onClose');
    onClose && onClose(scene);

    // очистка памяти от колбеков
    this.deleteCallbacks(sceneId);

    /* для очистки аппстейта от закрытых записей */
    const sceneType = scene && scene.get('type');
    if (
      sceneType === SCENE_TYPE.RECORD ||
      sceneType === SCENE_TYPE.RECORDS_BATCH_UPDATE ||
      sceneType === SCENE_TYPE.RECORDS_BATCH_DELETE
    ) {
      const catalogId = scene.getIn(['params', 'catalogId']);
      const recordId = scene.getIn(['params', 'recordId']);
      this.clearRecord(catalogId, recordId);
    }

    this.changed();
  },

  /* снятие флага shouldClose */
  cancelDeleteScene() {
    this.get('scenes')
      .filter((scene) => scene.get('shouldClose') === true)
      .forEach((_scene, sceneId) => {
        this.setIn(['scenes', sceneId, 'shouldClose'], false);
      });
    this.changed();
  },

  // store scenes callbacks
  setCallbacks(sceneId, callbacks) {
    if (!this.callbacks[sceneId]) {
      this.callbacks[sceneId] = {};
    }
    this.callbacks[sceneId] = _.merge(this.callbacks[sceneId], callbacks);
  },
  getCallbacks(sceneId) {
    return this.callbacks[sceneId] ? this.callbacks[sceneId] : null;
  },
  getCallback(sceneId, callbackName) {
    const callbacks = this.getCallbacks(sceneId);
    return callbacks && callbacks[callbackName];
  },
  deleteCallbacks(sceneId) {
    if (this.callbacks[sceneId]) delete this.callbacks[sceneId];
  },

  /*
    Манипуляция со сценами (открытие, закрытие)
  */

  async openNewRecord(params, values, callbacks, container = SCENE_CONTAINER.POPUP) {
    let { parentSceneId, catalogId, viewId } = params;

    // полседняя активная сцена будет установленна в качестве сцены-родителя
    parentSceneId = parentSceneId || this.getCurrentParentScene();

    // мержим значения из фильтров
    const filterValues = this._getFilterValues(parentSceneId, catalogId, viewId);
    values = _.assign({}, filterValues, values);

    // создание новой записи
    const recordId = guid.raw();
    recordActions.generateNewRecord(params.catalogId, recordId, values);

    // создание сцены с новой записью в модальном окне
    this.openRecord({ ...params, recordId }, callbacks, container);
  },

  _getFilterValues(sceneId, catalogId, viewId) {
    let filterValues = {};

    // из родительской сцены получаем значения фильтров
    const viewFilters = this.getIn(['scenes', sceneId, 'views', viewId, 'filters']);

    const sceneFilters = this.getIn(['scenes', sceneId, 'data', 'filters']);
    const catalogs = this.get('catalogs');

    // user нужен для обработки Сотрудник.Я...
    const user = this.get('user');

    // объединение фильтров с двух разных источников
    let filters = filtersUtils.mergeFilters(viewFilters, sceneFilters);
    // приводим фильтры в один формат js
    filters = filters && filters.toJS ? filters.toJS() : filters;

    if (filters) {
      filterValues = getFilterValues(filters, catalogs, catalogId, user);
    }

    return filterValues;
  },

  openCloneRecord(params, callbacks, container = SCENE_CONTAINER.POPUP) {
    // определение клонированной записи, в качестве нового элемента
    const newRecordId = guid.raw();
    this.cloneRecord(params.catalogId, params.recordId, newRecordId);

    // создание сцены с новой записью в модальном окне
    this.openRecord({ ...params, recordId: newRecordId }, callbacks, container);
  },

  _createRecordParentSceneInModal(sceneId) {
    let scene = this.getIn(['scenes', sceneId]);

    const sceneContainer = scene && scene.get('container');
    const sceneType = scene && scene.get('type');

    if (sceneContainer === SCENE_CONTAINER.WINDOW && sceneType === SCENE_TYPE.RECORD) {
      const parentSceneId = guid.raw();
      scene = scene
        .set('container', SCENE_CONTAINER.POPUP)
        .set('sceneId', parentSceneId)
        .set('notReturnInitialValues', true);
      this.setIn(['scenes', parentSceneId], scene);

      const onClose = () => {
        this.deleteScene(parentSceneId);
      };

      return { parentSceneId, onClose };
    }
    return { parentSceneId: sceneId };
  },

  async openRecord(params, callbacks = {}, container = SCENE_CONTAINER.POPUP) {
    let { parentSceneId, title, ...otherParams } = params;

    // полседняя активная сцена будет установленна в качестве сцены-родителя
    parentSceneId = parentSceneId || this.getCurrentParentScene();

    // создание сцены
    const type = SCENE_TYPE.RECORD;
    let sceneId = guid.raw();

    const catalogSetByNumbers = /^\d+$/.test(params.catalogId);
    const catalogExist = this.getIn(['catalogs', params.catalogId]);

    // загружаем каталог, если его нет в стейте (кейс с генерируемыми каталогами через апи)
    if (catalogSetByNumbers && !catalogExist) {
      // Если каталог задан не числовым id, то отдельно получаем его реальный id и секцию
      await makeApiRequest(`catalogs/${params.catalogId}`);
    }

    // check if record opened from right panel and provides parentSceneId
    if (container == SCENE_CONTAINER.POPUP) {
      const { parentSceneId: newParentSceneId, onClose } = this._createRecordParentSceneInModal(parentSceneId);
      parentSceneId = newParentSceneId;

      // set callback, which will remove parentScene
      if (callbacks && callbacks.onClose) {
        const initialOnCloseCallback = callbacks.onClose;

        callbacks.onClose = (...args) => {
          onClose && onClose(...args);
          initialOnCloseCallback(...args);
        };
      } else {
        callbacks.onClose = onClose;
      }
    }

    // проверяем не открыт ли же этот рекорд в корневом уровне
    const scenes = this.get('scenes');
    const sameScene =
      scenes &&
      scenes.find(
        (s) =>
          s.get('container') == SCENE_CONTAINER.POPUP &&
          s.get('parentSceneId') == '' &&
          s.get('type') == type &&
          s.getIn(['params', 'catalogId']) == params.catalogId &&
          s.getIn(['params', 'recordId']) == params.recordId,
      );
    // запрещаем открывать повтрно Записи только в Попапе и только на 0 уровне
    if (sameScene && container === SCENE_CONTAINER.POPUP && parentSceneId == '') {
      sceneId = sameScene.get('sceneId');
    } else {
      // create new Scene for Record
      this.createScene({ sceneId, params: otherParams, type, title, parentSceneId, container }, callbacks);
    }

    // изменяет активную сцену на текущую
    this.switchModal(sceneId);

    if (container === SCENE_CONTAINER.WINDOW) {
      /* прописать действия для открытия записи в окне, если установлен флаг container = window */
    } else if (container === SCENE_CONTAINER.POPUP) {
      this.setVisible(true);
    }
  },

  openRecordsBatchUpdate(parentSceneId, params, callbacks, container = SCENE_CONTAINER.POPUP) {
    // создание сцены
    const sceneId = guid.raw();
    const type = SCENE_TYPE.RECORDS_BATCH_UPDATE;
    // создание новой записи только для массового изменения
    const recordId = `batchUpdate:${guid.raw()}`;
    params.recordId = recordId;
    const batchUpdateRecord = {
      id: recordId,
      catalogId: params.catalogId,
      isNew: false,
    };

    const scene = this.getIn(['scenes', parentSceneId]);

    const data = this.formationFilters(params.viewId, scene);

    const record = RecordFactory.create(batchUpdateRecord);
    this.setIn(['records', params.catalogId, recordId], record);

    this.createScene({ sceneId, params, data, type, parentSceneId, container }, callbacks);

    const recordIds = params.recordIds;

    if (Array.isArray(recordIds) && recordIds.length > 0) {
      this.setIn(['scenes', sceneId, 'data', 'recordIds'], Immutable.fromJS(recordIds));
      this.setIn(['scenes', sceneId, 'recordsCount'], recordIds.length);
      this._setBatchStatus(sceneId, BATCH_RECORDS_STATUS.READY);
    }

    if (Array.isArray(params.excludeRecordIds) && params.excludeRecordIds.length > 0) {
      this.setIn(
        ['scenes', sceneId, 'data', 'excludeRecordIds'],
        Immutable.fromJS(params.excludeRecordIds.map(String)),
      );
    }

    // изменяет активную сцену на текущую
    this.switchModal(sceneId);

    if (container === SCENE_CONTAINER.WINDOW) {
      /* прописать действия для открытия записи в окне, если установлен флаг container = window */
    } else if (container === SCENE_CONTAINER.POPUP) {
      this.setVisible(true);
    }
  },

  openRecordsBatchDelete(parentSceneId, params, callbacks, container = SCENE_CONTAINER.POPUP) {
    // создание сцены
    const sceneId = guid.raw();
    const type = SCENE_TYPE.RECORDS_BATCH_DELETE;

    const scene = this.getIn(['scenes', parentSceneId]);

    const data = this.formationFilters(params.viewId, scene);

    this.createScene({ sceneId, params, data, type, parentSceneId, container }, callbacks);

    const recordIds = params.recordIds;

    if (Array.isArray(recordIds) && recordIds.length > 0) {
      this.setIn(['scenes', sceneId, 'data', 'recordIds'], Immutable.fromJS(recordIds));
      this.setIn(['scenes', sceneId, 'recordsCount'], recordIds.length);
      this._setBatchStatus(sceneId, BATCH_RECORDS_STATUS.READY);
    }

    if (Array.isArray(params.excludeRecordIds) && params.excludeRecordIds.length > 0) {
      this.setIn(
        ['scenes', sceneId, 'data', 'excludeRecordIds'],
        Immutable.fromJS(params.excludeRecordIds.map(String)),
      );
    }

    // изменяет активную сцену на текущую
    this.switchModal(sceneId);

    if (container === SCENE_CONTAINER.WINDOW) {
      /* прописать действия для открытия записи в окне, если установлен флаг container = window */
    } else if (container === SCENE_CONTAINER.POPUP) {
      this.setVisible(true);
    }
  },

  copyViews(initialSceneId, currentSceneId) {
    if (!initialSceneId || !currentSceneId) return;

    const sameCatalog =
      this.getIn(['scenes', initialSceneId, 'params', 'catalogId']) ==
      this.getIn(['scenes', currentSceneId, 'params', 'catalogId']);
    const initialViews = this.getIn(['scenes', initialSceneId, 'views']);
    const currentViews = this.getIn(['scenes', currentSceneId, 'views']);

    // Проверяем что оба views существуют перед merge
    if (!currentViews || !initialViews) {
      return;
    }

    const views = currentViews.merge(initialViews);

    if (sameCatalog) {
      this.setIn(['scenes', currentSceneId, 'views'], views);
      this.changed();
    }
  },

  async openCatalog(params, data, callbacks, container = SCENE_CONTAINER.POPUP) {
    let { parentSceneId } = params;
    // полседняя активная сцена будет установленна в качестве сцены-родителя
    parentSceneId = parentSceneId || this.getCurrentParentScene();

    if (container == SCENE_CONTAINER.POPUP) {
      // создаем запись родительской сцены
      const { parentSceneId: newParentSceneId, onClose } = this._createRecordParentSceneInModal(parentSceneId); // получаем новую сцену, и колбэк onClose
      parentSceneId = newParentSceneId;

      if (callbacks && callbacks.onClose) {
        // если есть колбек, оставляем его
        const initialOnCloseCallback = callbacks.onClose;

        callbacks.onClose = (...args) => {
          onClose && onClose(...args);
          initialOnCloseCallback(...args);
        };
      } else {
        // если колбэка нет в аргументах, записываем в колбэк тот колбэк который мы получили из функции _createRecordParentSceneInModal
        callbacks.onClose = onClose;
      }
    }

    // создание сцены
    const type = SCENE_TYPE.CATALOG;
    const sceneId = guid.raw();

    this.createScene({ sceneId, params, data, type, parentSceneId, container }, callbacks);

    // изменяет активную сцену на текущую
    this.switchModal(sceneId);

    if (container === SCENE_CONTAINER.WINDOW) {
      /* прописать действия для открытия записи в окне, если установлен флаг container = window */
    } else if (container === SCENE_CONTAINER.POPUP) {
      this.setVisible(true);
    }

    await userSettingsActions.getUserSettingsForCatalog({
      catalogId: params.catalogId,
    });
    await apiActions.getCatalog({ catalogId: params.catalogId }, undefined, {
      sceneId,
    });
    await apiActions.getViews({ catalogId: params.catalogId }, undefined, {
      sceneId,
    });
  },

  // actions to manipulate active record
  closeScene(sceneId) {
    /* указатель на то, что все дочение и текущая сцены должны быть удалены */
    this._markSceneToDelete(sceneId);

    /* определение последнего элемента в дереве */
    const lastChildSceneId = this._findSceneToDelete();

    /* переключение сцены */
    this.switchModal(lastChildSceneId);

    this.changed();
  },

  _markSceneToDelete(sceneId) {
    /**
     * если сцена которую мы хотим закрыть является окном, то не трогай попапы
     * в противном случае оставляй как есть
     * если мы закрываем сцену типа window, то мы должны рекурсивно закрыть дочерние сцены типа window
     * если мы закрываем сцену типа popup, то мы должны рекурсивно закрыть дочерние сцены типа popup
     */

    this.setIn(['scenes', sceneId, 'shouldClose'], true);

    const currentSceneContainer = this.getIn(['scenes', sceneId, 'container']);

    this.get('scenes')
      .filter((scene) => scene.get('parentSceneId') === sceneId && scene.get('container') === currentSceneContainer)
      .forEach((_childScene, childSceneId) => {
        this._markSceneToDelete(childSceneId);
      });
  },

  _findSceneToDelete() {
    const scenesToDelete = this.get('scenes').filter((scene) => scene.get('shouldClose') === true);

    const lastChild = scenesToDelete.find(
      (scene, sceneId) => !scenesToDelete.find((scene) => scene.get('parentSceneId') === sceneId),
    );

    return lastChild ? lastChild.get('sceneId') : false;
  },

  switchModal(sceneId) {
    if (sceneId) {
      const scene = this.getIn(['scenes', sceneId]);
      const isPopup = scene && scene.get('container') === SCENE_CONTAINER.POPUP;
      if (isPopup) {
        this.setIn(['modal', 'activeScene'], sceneId);
      }
    } else {
      this.setVisible(false);
    }
    this.changed();
  },

  switchFirstModal() {
    const modalScene =
      this.get('scenes') &&
      this.get('scenes').find((scene) => scene.get('container') === SCENE_CONTAINER.POPUP && !scene.get('shouldClose'));
    const activeSceneId = modalScene && modalScene.get('sceneId');
    activeSceneId && this.switchModal(activeSceneId);
  },

  setVisible(visible) {
    this.setIn(['modal', 'visible'], visible);

    if (visible) {
      const activeScheneId = this.getIn(['modal', 'activeScene']);
      const activeScene = this.getIn(['scenes', activeScheneId]);
      if (!activeScene) {
        this.switchFirstModal();
      }
    }

    this.changed();
  },

  /*
    HELPERS
  */
  getCurrentParentScene() {
    const visible = this.getIn(['modal', 'visible']);
    return visible ? this.getIn(['modal', 'activeScene']) : '';
  },

  deleteRecordsFromScene(sceneId, viewMode) {
    if (viewMode) {
      // Удаляем записи только для конкретного viewMode
      const recordsKey = getRecordsKey(viewMode);
      this.deleteIn(['scenes', sceneId, recordsKey]);
    } else {
      // Удаляем записи для всех viewMode (обратная совместимость)
      this.deleteIn(['scenes', sceneId, 'records']);
      this.deleteIn(['scenes', sceneId, 'calendarRecords']);
      this.deleteIn(['scenes', sceneId, 'ganttRecords']);
      this.deleteIn(['scenes', sceneId, 'cardsRecords']);
      this.deleteIn(['scenes', sceneId, 'reportsRecords']);
      this.deleteIn(['scenes', sceneId, 'pivotRecords']);
    }
    this.changed();
  },

  deleteCatalogCompleted(result, params, data, query, res, actionParams) {
    const { catalogId } = params;

    /* при удалении каталога удаляются все сцены каталога и сцены его записей */
    const scenesToDelete = this.get('scenes').filter((scene) => scene.getIn(['params', 'catalogId']) === catalogId);

    /* поиск дочерних сцен */
    scenesToDelete.forEach((scene, sceneId) => {
      const children = this.get('scenes').filter((child) => child.get('parentSceneId') === sceneId);

      /* неудаленным дочерним записям заменяем родительскую сцену */
      children
        .filter((ch) => ch.getIn(['params', 'catalogId']) !== catalogId)
        .forEach((_ch, chSceneId) => {
          let parentSceneId;
          /* если сцена - каталог, то можно спокойно брать ее родительский элемент */
          if (scene.get('type') === SCENE_TYPE.CATALOG) {
            parentSceneId = scene.get('parentSceneId');
            /* если сцена - запись, то нужно взять ее родителя (удаляемый каталог) и от него взять родительскую сцену */
          } else if (scene.get('type') === SCENE_TYPE.RECORD) {
            const catalogSceneId = scene.get('parentSceneId');
            parentSceneId = this.getIn(['scenes', catalogSceneId, 'parentSceneId']);
          }
          this.setIn(['scenes', chSceneId, 'parentSceneId'], parentSceneId);
        });
      this.deleteScene(sceneId);
    });

    this.changed();
  },

  // default scene title for breadcrumbs
  getRecord(params, query, actionParams) {
    const recordsScenes = this.get('scenes').filter(
      (scene) =>
        scene.getIn(['params', 'catalogId']) === params.catalogId &&
        scene.getIn(['params', 'recordId']) === params.recordId &&
        scene.get('type') === SCENE_TYPE.RECORD,
    );
    recordsScenes &&
      recordsScenes.forEach((scene, sceneId) => {
        // get title
        const currentTitle = this.getIn(['scenes', sceneId, 'title']);
        if (!currentTitle) {
          // set scene title
          this.setIn(['scenes', sceneId, 'title'], `#${params.recordId}`);
        }
      });

    this.changed();
  },

  getRecordCompleted(body, params, data, query, res, actionParams) {
    this.syncRecordInCatalogScenes(params, body);

    const recordsScenes = this.get('scenes').filter(
      (scene) =>
        scene.getIn(['params', 'catalogId']) === params.catalogId &&
        scene.getIn(['params', 'recordId']) === params.recordId &&
        scene.get('type') === SCENE_TYPE.RECORD,
    );
    recordsScenes &&
      recordsScenes.forEach((scene, sceneId) => {
        // set scene title
        this.setIn(['scenes', sceneId, 'title'], body.title);
        /* снимаем флаг о необходимости получения записи */
        this.setShouldReload(sceneId, false);
      });

    this.changed();
  },

  syncRecordInCatalogScenes(params, data) {
    const scenes = this.get('scenes');

    // обновление записи каталога, пришедшей записи с сервера
    // обновление полученнной записи в списке записей каталогов
    /*
      полученная запись может присутствовать в различных сценах каталога,
      поэтому заменим "старые" значения полей записи на "новые" (пришедшие) значения полей
    */

    const values = data && data.values;
    const newValues = values ? (Immutable.Map.isMap(values) ? values : Immutable.fromJS(values)) : values;

    scenes &&
      scenes
        .filter(
          (scene) =>
            scene.getIn(['params', 'catalogId']) === params.catalogId && scene.get('type') === SCENE_TYPE.CATALOG,
        )
        .forEach((scene, sceneId) => {
          const key =
            scene.get('records') && scene.get('records').findKey((item) => item.get('id') === String(params.recordId));

          if (key || key === 0 || key === '0') {
            this.mergeIn(['scenes', sceneId, 'records', key, 'values'], newValues);
          }

          /* Гант держит выборку в своём ключе: у него собственная загрузка двумя сторонами
             от сегодня, поэтому общий records ему не подходит */
          const ganttRecords = scene.get('ganttRecords');
          const ganttKey = ganttRecords && ganttRecords.findKey((item) => item.get('id') === String(params.recordId));

          if (ganttKey || ganttKey === 0 || ganttKey === '0') {
            this.mergeIn(['scenes', sceneId, 'ganttRecords', ganttKey, 'values'], newValues);
          }

          /* синхронизация предлагаемых записей для календаря */
          const suggestedRecords = scene.get('suggestedRecords');

          if (suggestedRecords) {
            const suggestedRecordKey = suggestedRecords.findKey((r) => r.get('id') === params.recordId);

            if (suggestedRecordKey || suggestedRecordKey === 0 || suggestedRecordKey === '0') {
              this.mergeIn(['scenes', sceneId, 'suggestedRecords', suggestedRecordKey, 'values'], newValues);
              this.changed();
            }
          }
        });

    // обновление записей каталогов, пришедших связанных записей полученной записи
    // обновление полученнных связанных записей в списках записей каталогов
    /*
      "связанная запись" может является записью каталога, так и связью в другой записи

      Полученная запись может содержать в себе другие связанные записи.
      Нужно найти все эти связанные записи в списке записей во всех сценах каталога и заменить "старые"
      значения полей на "новые", пришедшие в расширенных полях полученной записи.
    */

    const linkedRecords = this.getLinkedRecords(params.catalogId, newValues);

    linkedRecords.forEach((linkedRecord) => {
      const catalogId = linkedRecord.get('catalogId');
      const recordId = linkedRecord.get('recordId');
      const values = linkedRecord.get('recordValues');

      /*
        проверка на то, что значения связанной записи действительно пришли с сервера, тк на 2 уровне вложенности связей recordValues отсутствуют
        если в связях указана сама запись, то нет смысла проводить по ней синхронизацию
      */
      values && this.syncRecordInCatalogScenes({ catalogId, recordId }, { values });
    });

    // обновление связанных записей в записи в списке записей каталогов, полученной записи
    // обновление полученной записи в связанной записи у записи в списке записей каталогов
    /*
      полученная запись может быть связанной записью для многих других записей.
      Эти "другие записи" могут содержаться во многих каталогах, открытых в разных сценах.
      поэтому в "других записях" в сценах заменим тайтлы связанных записей равных полученной записью
    */

    //   let needReload;
    //   if (linkFields && linkFields.size > 0) {
    //     sceneRecords &&
    //       sceneRecords.forEach(r => {
    //         if (needReload) {
    //           return;
    //         }
    //         linkFields.forEach(f => {
    //           if (needReload) {
    //             return;
    //           }
    //           r.getIn(["values", f.get("id")]) &&
    //             r.getIn(["values", f.get("id")]).forEach(v => {
    //               if (needReload) {
    //                 return;
    //               }
    //               if (f.get("type") === FIELD_TYPES.OBJECT) {
    //                 if (
    //                   v.get("catalogId") === params.catalogId &&
    //                   v.get("recordId") === params.recordId
    //                 ) {
    //                   needReload = true;
    //                 }
    //               } else {
    //                 if (v.get("id") === params.recordId) {
    //                   needReload = true;
    //                 }
    //               }
    //             });
    //         });
    //       });
    //       /* прописать тайтл связанной записи */
    //   }
  },

  /* в сцене заменяет рекорды с guid на id  с сервера (newRecordId) */
  createRecordCompleted(
    record,
    { catalogId },
    data,
    // eslint-disable-next-line no-empty-pattern
    {},
    response,
    { recordId, sceneId, silent = false } = {}, // silent - означает тихое обновленеи те те случаи когда ререндер компонентов не нужен ()
  ) {
    const newRecordId = record.id;

    // update record id in modal opened records collection
    if (!newRecordId) {
      return;
    }

    // update recordId from Guid to real Id
    /* при создании записи через вложенное поле без создания всплывающего окна, у новосозданной записи не будет сцены, поэтому искать новые записи надежней по предыдущему айди записи */
    this.get('scenes').forEach((scene, sceneId) => {
      const newRecord = scene.getIn(['params', 'recordId']) == recordId;
      if (newRecord) {
        this.setIn(['scenes', sceneId, 'params', 'recordId'], newRecordId);
      }
    });

    // получить текущую сцену
    const scene = sceneId && this.getIn(['scenes', sceneId]);
    // проверяем есть ли текущая сцена
    if (scene) {
      // доастать парент сцен айди
      const parentSceneId = scene.get('parentSceneId');
      // получить парент сцен
      const parentScene = this.getIn(['scenes', parentSceneId]);
      // Проверяем есть ли у сцены родительская сцена и её тип каталог
      if (parentSceneId && parentScene.get('type') === SCENE_TYPE.CATALOG) {
        // Проверяем есть ли обработчик onCreateRecord у родительской сцены и вызываем его
        const onCreateRecord = this.getCallback(parentSceneId, 'onCreateRecord');
        onCreateRecord && onCreateRecord({ ...record, parentSceneId });
      }

      /* Вызов колбека  */
      const onCreateRecord = this.getCallback(sceneId, 'onCreateRecord');
      onCreateRecord && onCreateRecord({ ...record, sceneId });
    }

    /* обновление списка записей всех сцен этого каталога */
    if (!silent) {
      this.get('scenes')
        .filter(
          (scene) => scene.getIn(['params', 'catalogId']) === catalogId && scene.get('type') === SCENE_TYPE.CATALOG,
        )
        .forEach((_scene, sceneId) => {
          this.setShouldReload(sceneId, true);
        });

      this.changed();
    }
  },

  updateRecordCompleted(result, params, data, query, res, actionParams = {}) {
    const { silent = false } = actionParams;
    this.syncRecordInCatalogScenes(params, result);
    !silent && this.changed();
  },

  deleteRecordCompleted(result, params, data, query, res, actionParams = {}) {
    const { catalogId, recordId } = params;
    const { silent = false } = actionParams;

    const scenesToDelete = this.get('scenes').filter(
      (scene) =>
        scene.getIn(['params', 'catalogId']) === catalogId &&
        scene.getIn(['params', 'recordId']) === recordId &&
        scene.get('type') === SCENE_TYPE.RECORD,
    );

    /* подмена родителя на деда */
    scenesToDelete.forEach((_scene, sceneId) => {
      const children = this.get('scenes').filter((child) => child.get('parentSceneId') === sceneId);

      const newParentId = this.getParentForDeletedRecord(sceneId, scenesToDelete);

      /* может произойти ситуация, когда родительская запись и сама запись являются одним и тем же элементом */
      children.forEach((ch, chSceneId) => {
        this.setIn(['scenes', chSceneId, 'parentSceneId'], newParentId);
      });
    });

    /* удаление сцен */
    scenesToDelete.forEach((scene, sceneId) => {
      this.deleteScene(sceneId);
    });

    /* обновление записей в каталоге */
    if (!silent) {
      this.get('scenes')
        .filter(
          (scene) => scene.getIn(['params', 'catalogId']) === catalogId && scene.get('type') === SCENE_TYPE.CATALOG,
        )
        .forEach((_scene, sceneId) => {
          this.setShouldReload(sceneId, true);
        });

      this.changed();
    }
  },

  getParentForDeletedRecord(sceneId, scenesToDelete) {
    const parentSceneId = this.getIn(['scenes', sceneId, 'parentSceneId']);

    /* если родительская сцена не является удаляемой или если она отсутствует */
    if (!scenesToDelete.get(parentSceneId)) {
      return parentSceneId;
    }
    return this.getParentForDeletedRecord(parentSceneId, scenesToDelete);
  },

  getRecordsCompleted(records, params, data, query, response, actionParams) {
    if (!(actionParams && actionParams.sceneId)) {
      return;
    }

    const { sceneId, viewMode = 'table' } = actionParams;
    const scene = this.getIn(['scenes', sceneId]);

    // Получаем ключи для изоляции данных разных представлений
    const queryKey = getQueryKey(viewMode);
    const recordsKey = getRecordsKey(viewMode);
    const recordsCountKey = getRecordsCountKey(viewMode);
    const recordsCountNewMessagesKey = getRecordsCountNewMessagesKey(viewMode);
    const allRecordsLoadedKey = getAllRecordsLoadedKey(viewMode);
    const loadingKey = getLoadingKey(viewMode);
    const loadingErrorKey = getLoadingErrorKey(viewMode);

    const currentQuery = scene && scene.get(queryKey);
    // TODO: remove this workaround
    // если currentQuery не задан, скорее всего это состояние обновления страницы
    // т.е. когда страницу обновляют, то catalog еще не сушествует и
    // поэтому в него никто не запишет query
    if (scene && currentQuery && !Immutable.is(Immutable.fromJS(query), currentQuery)) {
      return;
    }
    const offset = query.offset || 0;
    const appendRecords = offset > 0 || actionParams.appendRecords;
    let recordsMap = {};

    records.map((c, i) => (recordsMap[offset + i] = RecordFactory.create(c)));
    recordsMap = Immutable.Map(recordsMap);

    if (appendRecords) {
      const existingRecords = scene && scene.get(recordsKey);
      if (existingRecords) {
        recordsMap = existingRecords.merge(recordsMap);
      }
    }

    this.setIn(['scenes', sceneId, recordsKey], recordsMap);

    // total records count
    this.setIn(['scenes', sceneId, recordsCountKey], Number(response.headers['x-total-count']) || 0);
    // total records count with new messages
    this.setIn(['scenes', sceneId, recordsCountNewMessagesKey], Number(response.headers['x-total-newmessages']) || 0);

    // loading state - обновляем только для текущего viewMode
    const loadingState = {
      [allRecordsLoadedKey]: records.length < query.limit,
      [loadingKey]: false,
      [loadingErrorKey]: null,
    };
    this.mergeIn(['scenes', sceneId], loadingState);

    this.setShouldReload(sceneId, false);
    this.changed();
  },

  getRecordsFailed(err, params, data, query, actionParams) {
    if (!(actionParams && actionParams.sceneId)) {
      return;
    }

    const { sceneId, viewMode = 'table' } = actionParams;

    if (!this.getIn(['scenes', sceneId])) {
      return;
    }

    const loadingKey = getLoadingKey(viewMode);
    const loadingErrorKey = getLoadingErrorKey(viewMode);
    const errText = _.isObject(err) && err.text ? err.text : '';

    this.setIn(['scenes', sceneId, loadingKey], false);
    this.setIn(['scenes', sceneId, loadingErrorKey], Immutable.fromJS(errText));
    this.changed();
  },

  // navigare to prev/next record ib Record scene
  getNavigateRecords(sceneId, currentRecordId, callback) {
    const prevRecordId = this._getNavigateRecord(sceneId, currentRecordId, -1);
    const nextRecordId = this._getNavigateRecord(sceneId, currentRecordId, 1);
    callback && callback({ prevRecordId, nextRecordId });
  },
  _getNavigateRecord(sceneId, currentRecordId, step = 1) {
    let recordId = null;
    const parentSceneId = this.getIn(['scenes', sceneId, 'parentSceneId']);
    const parentScene = this.getIn(['scenes', parentSceneId]);

    if (parentScene && parentScene.get('type') == SCENE_TYPE.CATALOG) {
      const records = parentScene.get('records');
      const recordsCount = parentScene.get('recordsCount');

      const _getRecordId = (recordIndex) => {
        if (recordIndex < 0 && step == -1) return null; // already on first
        if (recordIndex >= recordsCount && step == 1) return null; // already on last

        const record = records.get(String(recordIndex));
        if (record) {
          return record.get('id');
        }
        return undefined;
      };

      let currentRecordIndex = records && records.findKey((r) => r.get('id') === currentRecordId);
      if (currentRecordIndex !== undefined) {
        currentRecordIndex = currentRecordIndex * 1 + step;
        recordId = _getRecordId(currentRecordIndex);

        // check for next record to preload data
        if (recordId) {
          currentRecordIndex = currentRecordIndex * 1 + step;
          const nextRecordId = _getRecordId(currentRecordIndex);
          if (nextRecordId === undefined) {
            // need load
            // load records
            const catalogId = parentScene.getIn(['params', 'catalogId']);
            const viewId = parentScene.getIn(['query', 'viewId']) || '$new'; // единственный способ получить фильтры, так как вид не содержится в параметрах
            const limit = 10;
            recordActions.requestForRecords(catalogId, parentSceneId, {
              offset: step == 1 ? currentRecordIndex - 1 : currentRecordIndex + 1 - limit,
              limit,
              viewId,
            });
          }
        }
      }
    }

    return recordId;
  },
};

export default sceneMixin;
